import { PRStatus, PRItemStatus } from '@prisma/client';
import { loadPersistentData, savePersistentData } from './fileStorage';
import { syncPRToDatabase, syncAuditLogToDatabase } from './dbBridge';

export interface PRItemRecord {
  id: string;
  prId: string;
  itemId: string;
  itemCode: string;
  itemName: string;
  category: string;
  requestedQty: number;
  approvedQty: number;
  allocatedQty: number;
  uomId: string;
  uomCode: string;
  estimatedPrice: number;
  status: PRItemStatus;
  notes?: string;
  rejectionReason?: string;
  reappliedPrId?: string;
  reappliedPrNumber?: string;
  reappliedAt?: string;
  linkedPOs?: Array<{
    id: string;
    poNumber: string;
    status: string;
    orderedQty?: number;
  }>;
}

export interface PRAuditRecord {
  id: string;
  entityType: string;
  entityId: string;
  fromStatus?: string | null;
  toStatus?: string | null;
  actorId?: string | null;
  actorName?: string | null;
  actorRole?: string | null;
  action: string;
  reason?: string | null;
  metadata?: Record<string, unknown>;
  timestamp: string;
}

export interface PRRecord {
  id: string;
  prNumber: string;
  title?: string; // Judul / Tujuan Pengadaan
  date: string;
  requesterId: string;
  requesterName: string;
  department: string;
  notes?: string;
  status: PRStatus;
  items: PRItemRecord[];
  isRevision?: boolean; // Menandai jika PR sedang dalam status revisi
  revisionNotes?: string; // Catatan revisi dari Owner/Approver
  createdAt: string;
  updatedAt: string;
}

// Master Items catalog for selection
export { MASTER_ITEMS } from './masterItems';
import { MASTER_ITEMS } from './masterItems';

export function getAllMasterItems(): typeof MASTER_ITEMS {
  const persisted = loadPersistentData<typeof MASTER_ITEMS>('item-database.json', MASTER_ITEMS);
  for (const it of MASTER_ITEMS) {
    if (!persisted.some((p) => p.id === it.id || p.code === it.code)) {
      persisted.push(it);
    }
  }
  return persisted;
}

// Initial mock PR dataset
const initialPrDatabase: PRRecord[] = [
  {
    id: 'pr-001',
    prNumber: 'PR-2026-09-001',
    title: 'Restock Kemasan Karton Box & Bubble Wrap Q4',
    date: '2026-09-14T08:30:00.000Z',
    requesterId: 'usr-purchaser-01',
    requesterName: 'Siti Rahma',
    department: 'Procurement & Logistics',
    notes: 'Urgent packaging material replenishment for Q4 production spike',
    status: PRStatus.SUBMITTED,
    items: [
      {
        id: 'pri-001-1',
        prId: 'pr-001',
        itemId: 'itm-001',
        itemCode: 'ITM-001',
        itemName: 'Corrugated Carton Box 40x30x20',
        category: 'Packaging',
        requestedQty: 1000,
        approvedQty: 0,
        allocatedQty: 0,
        uomId: 'uom-001',
        uomCode: 'PCS',
        estimatedPrice: 8500,
        status: PRItemStatus.PENDING,
        notes: 'Carton specification test approved by QA',
      },
      {
        id: 'pri-001-2',
        prId: 'pr-001',
        itemId: 'itm-002',
        itemCode: 'ITM-002',
        itemName: 'Bubble Wrap 1.25m x 50m',
        category: 'Packaging',
        requestedQty: 50,
        approvedQty: 0,
        allocatedQty: 0,
        uomId: 'uom-002',
        uomCode: 'ROLL',
        estimatedPrice: 165000,
        status: PRItemStatus.PENDING,
        notes: 'Heavy duty bubble wrap for fragile product lines',
      },
      {
        id: 'pri-001-3',
        prId: 'pr-001',
        itemId: 'itm-004',
        itemCode: 'ITM-004',
        itemName: 'Industrial Opp Tape 2 inch Clear',
        category: 'Consumables',
        requestedQty: 200,
        approvedQty: 0,
        allocatedQty: 0,
        uomId: 'uom-001',
        uomCode: 'PCS',
        estimatedPrice: 12500,
        status: PRItemStatus.PENDING,
        notes: 'Monthly tape allocation',
      },
    ],
    createdAt: '2026-09-14T08:30:00.000Z',
    updatedAt: '2026-09-14T08:45:00.000Z',
  },
  {
    id: 'pr-002',
    prNumber: 'PR-2026-09-002',
    title: 'Pengadaan Stretch Film & Pallet Kayu Gudang',
    date: '2026-09-12T10:15:00.000Z',
    requesterId: 'usr-purchaser-01',
    requesterName: 'Siti Rahma',
    department: 'Procurement & Logistics',
    notes: 'Warehouse operations consumables replenishment',
    status: PRStatus.PARTIALLY_APPROVED,
    items: [
      {
        id: 'pri-002-1',
        prId: 'pr-002',
        itemId: 'itm-003',
        itemCode: 'ITM-003',
        itemName: 'Stretch Film 500mm x 250m',
        category: 'Packaging',
        requestedQty: 80,
        approvedQty: 80,
        allocatedQty: 0,
        uomId: 'uom-002',
        uomCode: 'ROLL',
        estimatedPrice: 92000,
        status: PRItemStatus.APPROVED,
        notes: 'Approved at full quota',
      },
      {
        id: 'pri-002-2',
        prId: 'pr-002',
        itemId: 'itm-005',
        itemCode: 'ITM-005',
        itemName: 'Standard Wooden Pallet 1200x1000mm',
        category: 'Warehouse Equipment',
        requestedQty: 100,
        approvedQty: 0,
        allocatedQty: 0,
        uomId: 'uom-001',
        uomCode: 'PCS',
        estimatedPrice: 225000,
        status: PRItemStatus.REJECTED,
        notes: 'Current wooden pallet inventory is sufficient until November',
        rejectionReason: 'Surplus existing stock in warehouse zone B',
      },
    ],
    createdAt: '2026-09-12T10:15:00.000Z',
    updatedAt: '2026-09-12T14:30:00.000Z',
  },
  {
    id: 'pr-003',
    prNumber: 'PR-2026-09-003',
    title: 'Pengadaan Pallet Standar & Lakban Operasional',
    date: '2026-09-10T09:00:00.000Z',
    requesterId: 'usr-purchaser-01',
    requesterName: 'Siti Rahma',
    department: 'Manufacturing Ops',
    notes: 'Q3 standard raw material and pallets renewal batch',
    status: PRStatus.APPROVED,
    items: [
      {
        id: 'pri-003-1',
        prId: 'pr-003',
        itemId: 'itm-005',
        itemCode: 'ITM-005',
        itemName: 'Standard Wooden Pallet 1200x1000mm',
        category: 'Warehouse Equipment',
        requestedQty: 40,
        approvedQty: 40,
        allocatedQty: 40,
        uomId: 'uom-001',
        uomCode: 'PCS',
        estimatedPrice: 225000,
        status: PRItemStatus.APPROVED,
      },
      {
        id: 'pri-003-2',
        prId: 'pr-003',
        itemId: 'itm-004',
        itemCode: 'ITM-004',
        itemName: 'Industrial Opp Tape 2 inch Clear',
        category: 'Consumables',
        requestedQty: 100,
        approvedQty: 100,
        allocatedQty: 100,
        uomId: 'uom-001',
        uomCode: 'PCS',
        estimatedPrice: 12500,
        status: PRItemStatus.APPROVED,
      },
    ],
    createdAt: '2026-09-10T09:00:00.000Z',
    updatedAt: '2026-09-10T15:20:00.000Z',
  },
  {
    id: 'pr-004',
    prNumber: '[Draf Belum Terbit]',
    title: 'Pengadaan Pita Barcode Thermal Printer Gudang',
    date: '2026-09-14T11:00:00.000Z',
    requesterId: 'usr-purchaser-01',
    requesterName: 'Siti Rahma',
    department: 'Logistics',
    notes: 'Draft requisition for barcode ribbons and wrap',
    status: PRStatus.DRAFT,
    items: [
      {
        id: 'pri-004-1',
        prId: 'pr-004',
        itemId: 'itm-006',
        itemCode: 'ITM-006',
        itemName: 'Heavy Duty Thermal Transfer Ribbon',
        category: 'Consumables',
        requestedQty: 30,
        approvedQty: 0,
        allocatedQty: 0,
        uomId: 'uom-002',
        uomCode: 'ROLL',
        estimatedPrice: 110000,
        status: PRItemStatus.PENDING,
        notes: 'Backup ribbons for barcode shipping printers',
      },
    ],
    createdAt: '2026-09-14T11:00:00.000Z',
    updatedAt: '2026-09-14T11:00:00.000Z',
  },
  {
    id: 'pr-005',
    prNumber: 'PR-2026-09-005',
    title: 'Pengadaan Bahan Baku Kain Rayon & Aksesoris Kemeja',
    date: '2026-09-15T08:00:00.000Z',
    requesterId: 'usr-purchaser-01',
    requesterName: 'Siti Rahma',
    department: 'Garment Manufacturing Division',
    notes: 'Pengadaan bahan baku kain dan aksesoris garmen untuk lini produksi kemeja ekspor batch 102',
    status: PRStatus.SUBMITTED,
    items: [
      {
        id: 'pri-005-1',
        prId: 'pr-005',
        itemId: 'itm-007',
        itemCode: 'ITM-007',
        itemName: 'Kain Katun Rayon',
        category: 'Raw Materials',
        requestedQty: 800,
        approvedQty: 0,
        allocatedQty: 0,
        uomId: 'uom-003',
        uomCode: 'METER',
        estimatedPrice: 20000,
        status: PRItemStatus.PENDING,
        notes: 'Sertifikasi OEKO-TEX standar ekspor',
      },
      {
        id: 'pri-005-2',
        prId: 'pr-005',
        itemId: 'itm-009',
        itemCode: 'ITM-009',
        itemName: 'Benang Jahit Spun Polyester 40/2',
        category: 'Accessories',
        requestedQty: 150,
        approvedQty: 0,
        allocatedQty: 0,
        uomId: 'uom-001',
        uomCode: 'PCS',
        estimatedPrice: 14500,
        status: PRItemStatus.PENDING,
        notes: '100 pcs putih, 50 pcs hitam',
      },
      {
        id: 'pri-005-3',
        prId: 'pr-005',
        itemId: 'itm-008',
        itemCode: 'ITM-008',
        itemName: 'Kancing Kemeja 4 Lubang',
        category: 'Accessories',
        requestedQty: 5000,
        approvedQty: 0,
        allocatedQty: 0,
        uomId: 'uom-001',
        uomCode: 'PCS',
        estimatedPrice: 500,
        status: PRItemStatus.PENDING,
        notes: 'Diameter 11mm pearl white',
      },
      {
        id: 'pri-005-4',
        prId: 'pr-005',
        itemId: 'itm-014',
        itemCode: 'ITM-014',
        itemName: 'Label Woven Satin Brand PT XYZ',
        category: 'Accessories',
        requestedQty: 3000,
        approvedQty: 0,
        allocatedQty: 0,
        uomId: 'uom-001',
        uomCode: 'PCS',
        estimatedPrice: 350,
        status: PRItemStatus.PENDING,
        notes: 'Label kerah utama kualitas damask',
      },
      {
        id: 'pri-005-5',
        prId: 'pr-005',
        itemId: 'itm-012',
        itemCode: 'ITM-012',
        itemName: 'Kain Spunlace Non-Woven Interlining',
        category: 'Raw Materials',
        requestedQty: 400,
        approvedQty: 0,
        allocatedQty: 0,
        uomId: 'uom-003',
        uomCode: 'METER',
        estimatedPrice: 18000,
        status: PRItemStatus.PENDING,
        notes: 'Kain keras pelapis kerah dan manset',
      },
    ],
    createdAt: '2026-09-15T08:00:00.000Z',
    updatedAt: '2026-09-15T08:15:00.000Z',
  },
  {
    id: 'pr-006',
    prNumber: 'PR-2026-09-006',
    title: 'Pengadaan Kemasan Polybag & Proteksi Ekspor',
    date: '2026-09-15T09:30:00.000Z',
    requesterId: 'usr-purchaser-01',
    requesterName: 'Siti Rahma',
    department: 'Warehouse & Packaging Department',
    notes: 'Pengadaan material pengemasan dan proteksi logistik ekspor kuartal 4',
    status: PRStatus.SUBMITTED,
    items: [
      {
        id: 'pri-006-1',
        prId: 'pr-006',
        itemId: 'itm-001',
        itemCode: 'ITM-001',
        itemName: 'Corrugated Carton Box 40x30x20',
        category: 'Packaging',
        requestedQty: 2500,
        approvedQty: 0,
        allocatedQty: 0,
        uomId: 'uom-001',
        uomCode: 'PCS',
        estimatedPrice: 8500,
        status: PRItemStatus.PENDING,
        notes: 'Karton box cetak logo standar ekspor',
      },
      {
        id: 'pri-006-2',
        prId: 'pr-006',
        itemId: 'itm-013',
        itemCode: 'ITM-013',
        itemName: 'Plastik Polybag Klip 30x40cm',
        category: 'Packaging',
        requestedQty: 5000,
        approvedQty: 0,
        allocatedQty: 0,
        uomId: 'uom-001',
        uomCode: 'PCS',
        estimatedPrice: 650,
        status: PRItemStatus.PENDING,
        notes: 'Plastik pembungkus satuan per potong garmen',
      },
      {
        id: 'pri-006-3',
        prId: 'pr-006',
        itemId: 'itm-002',
        itemCode: 'ITM-002',
        itemName: 'Bubble Wrap 1.25m x 50m',
        category: 'Packaging',
        requestedQty: 60,
        approvedQty: 0,
        allocatedQty: 0,
        uomId: 'uom-002',
        uomCode: 'ROLL',
        estimatedPrice: 165000,
        status: PRItemStatus.PENDING,
      },
      {
        id: 'pri-006-4',
        prId: 'pr-006',
        itemId: 'itm-004',
        itemCode: 'ITM-004',
        itemName: 'Industrial Opp Tape 2 inch Clear',
        category: 'Consumables',
        requestedQty: 350,
        approvedQty: 0,
        allocatedQty: 0,
        uomId: 'uom-001',
        uomCode: 'PCS',
        estimatedPrice: 12500,
        status: PRItemStatus.PENDING,
      },
      {
        id: 'pri-006-5',
        prId: 'pr-006',
        itemId: 'itm-015',
        itemCode: 'ITM-015',
        itemName: 'Silica Gel Desiccant 2gr White',
        category: 'Consumables',
        requestedQty: 80,
        approvedQty: 0,
        allocatedQty: 0,
        uomId: 'uom-004',
        uomCode: 'PACK',
        estimatedPrice: 25000,
        status: PRItemStatus.PENDING,
        notes: 'Penyerap kelembapan di dalam karton kontainer ekspor',
      },
      {
        id: 'pri-006-6',
        prId: 'pr-006',
        itemId: 'itm-020',
        itemCode: 'ITM-020',
        itemName: 'Stiker Barcode Thermal 100x50mm',
        category: 'Packaging',
        requestedQty: 45,
        approvedQty: 0,
        allocatedQty: 0,
        uomId: 'uom-002',
        uomCode: 'ROLL',
        estimatedPrice: 42000,
        status: PRItemStatus.PENDING,
        notes: 'Label barcode identifikasi karton',
      },
    ],
    createdAt: '2026-09-15T09:30:00.000Z',
    updatedAt: '2026-09-15T09:45:00.000Z',
  },
  {
    id: 'pr-007',
    prNumber: 'PR-2026-09-007',
    title: 'Pengadaan Jarum Jahit Industri & APD Operator',
    date: '2026-09-15T11:00:00.000Z',
    requesterId: 'usr-purchaser-01',
    requesterName: 'Siti Rahma',
    department: 'Maintenance & Workshop Division',
    notes: 'Kebutuhan consumables jarum mesin jahit dan perlengkapan gudang',
    status: PRStatus.SUBMITTED,
    items: [
      {
        id: 'pri-007-1',
        prId: 'pr-007',
        itemId: 'itm-016',
        itemCode: 'ITM-016',
        itemName: 'Jarum Mesin Jahit Groz-Beckert DPx5 #14',
        category: 'Consumables',
        requestedQty: 25,
        approvedQty: 0,
        allocatedQty: 0,
        uomId: 'uom-005',
        uomCode: 'BOX',
        estimatedPrice: 85000,
        status: PRItemStatus.PENDING,
        notes: 'Stok jarum cadangan lini jahit A & B',
      },
      {
        id: 'pri-007-2',
        prId: 'pr-007',
        itemId: 'itm-019',
        itemCode: 'ITM-019',
        itemName: 'Sarung Tangan Kerja Katun Rajut Bintik',
        category: 'Consumables',
        requestedQty: 120,
        approvedQty: 0,
        allocatedQty: 0,
        uomId: 'uom-001',
        uomCode: 'PASANG',
        estimatedPrice: 6500,
        status: PRItemStatus.PENDING,
        notes: 'Perlengkapan K3 operator bongkar muat gudang',
      },
      {
        id: 'pri-007-3',
        prId: 'pr-007',
        itemId: 'itm-006',
        itemCode: 'ITM-006',
        itemName: 'Heavy Duty Thermal Transfer Ribbon',
        category: 'Consumables',
        requestedQty: 20,
        approvedQty: 0,
        allocatedQty: 0,
        uomId: 'uom-002',
        uomCode: 'ROLL',
        estimatedPrice: 110000,
        status: PRItemStatus.PENDING,
      },
    ],
    createdAt: '2026-09-15T11:00:00.000Z',
    updatedAt: '2026-09-15T11:15:00.000Z',
  },
  {
    id: 'pr-008',
    prNumber: '[Draf Belum Terbit]',
    title: 'Pengadaan Bahan Baku Denim & Zipper Jaket Ekspor',
    date: '2026-09-15T13:00:00.000Z',
    requesterId: 'usr-purchaser-01',
    requesterName: 'Siti Rahma',
    department: 'Denim Production Division',
    notes: 'Draf pengadaan kain denim dan zipper metal untuk proyek jaket denim',
    status: PRStatus.DRAFT,
    isRevision: true,
    revisionNotes: 'Mohon kurangi kuantitas kain denim menjadi 800 meter sesuai kapasitas potong batch 1, dan pastikan zipper kuningan antik sesuai standar buyer.',
    items: [
      {
        id: 'pri-008-1',
        prId: 'pr-008',
        itemId: 'itm-011',
        itemCode: 'ITM-011',
        itemName: 'Kain Denim Stretch 12oz Indigo',
        category: 'Raw Materials',
        requestedQty: 1200,
        approvedQty: 0,
        allocatedQty: 0,
        uomId: 'uom-003',
        uomCode: 'METER',
        estimatedPrice: 45000,
        status: PRItemStatus.PENDING,
      },
      {
        id: 'pri-008-2',
        prId: 'pr-008',
        itemId: 'itm-010',
        itemCode: 'ITM-010',
        itemName: 'Resleting / Zipper YKK Metal 5 inch',
        category: 'Accessories',
        requestedQty: 600,
        approvedQty: 0,
        allocatedQty: 0,
        uomId: 'uom-001',
        uomCode: 'PCS',
        estimatedPrice: 4200,
        status: PRItemStatus.PENDING,
      },
      {
        id: 'pri-008-3',
        prId: 'pr-008',
        itemId: 'itm-017',
        itemCode: 'ITM-017',
        itemName: 'Tali Webbing Katun 2.5cm Tebal',
        category: 'Raw Materials',
        requestedQty: 50,
        approvedQty: 0,
        allocatedQty: 0,
        uomId: 'uom-002',
        uomCode: 'ROLL',
        estimatedPrice: 75000,
        status: PRItemStatus.PENDING,
      },
    ],
    createdAt: '2026-09-15T13:00:00.000Z',
    updatedAt: '2026-09-15T13:00:00.000Z',
  },
  {
    id: 'pr-009',
    prNumber: '[Draf Belum Terbit]',
    title: 'Pengadaan Hand Pallet Truck 3 Ton & Pallet Gudang',
    date: '2026-09-15T14:00:00.000Z',
    requesterId: 'usr-admin-01',
    requesterName: 'System Administrator',
    department: 'Facility & Warehouse Logistics',
    notes: 'Draf pengadaan alat angkut gudang',
    status: PRStatus.DRAFT,
    items: [
      {
        id: 'pri-009-1',
        prId: 'pr-009',
        itemId: 'itm-018',
        itemCode: 'ITM-018',
        itemName: 'Hand Pallet Truck 3 Ton Heavy Duty',
        category: 'Warehouse Equipment',
        requestedQty: 2,
        approvedQty: 0,
        allocatedQty: 0,
        uomId: 'uom-001',
        uomCode: 'UNIT',
        estimatedPrice: 3850000,
        status: PRItemStatus.PENDING,
      },
      {
        id: 'pri-009-2',
        prId: 'pr-009',
        itemId: 'itm-005',
        itemCode: 'ITM-005',
        itemName: 'Standard Wooden Pallet 1200x1000mm',
        category: 'Warehouse Equipment',
        requestedQty: 50,
        approvedQty: 0,
        allocatedQty: 0,
        uomId: 'uom-001',
        uomCode: 'PCS',
        estimatedPrice: 225000,
        status: PRItemStatus.PENDING,
      },
    ],
    createdAt: '2026-09-15T14:00:00.000Z',
    updatedAt: '2026-09-15T14:00:00.000Z',
  },
];

const prDatabase: PRRecord[] = loadPersistentData('pr-database.json', initialPrDatabase);

// Auto-reconciliation to ensure Sacred Domain Invariants:
// 1. If any item has allocatedQty > 0, parent PR MUST be APPROVED!
// 2. If PR status is SUBMITTED or DRAFT, ALL items must have allocatedQty === 0!
// 3. Any legacy PARTIALLY_APPROVED status is normalized to APPROVED.
for (const pr of prDatabase) {
  if ((pr.status as string) === 'PARTIALLY_APPROVED') {
    pr.status = PRStatus.APPROVED;
  }
  const hasAllocated = pr.items?.some((i) => (i.allocatedQty || 0) > 0);
  if (hasAllocated && pr.status !== PRStatus.APPROVED) {
    pr.status = PRStatus.APPROVED;
  }
  if (pr.status === PRStatus.SUBMITTED || pr.status === PRStatus.DRAFT) {
    for (const item of pr.items) {
      if ((item.allocatedQty || 0) > 0) {
        item.allocatedQty = 0;
      }
    }
  }
}
savePersistentData('pr-database.json', prDatabase);

const initialAuditLogsDatabase: PRAuditRecord[] = [
  {
    id: 'aud-001',
    entityType: 'PURCHASE_REQUEST',
    entityId: 'pr-001',
    fromStatus: null,
    toStatus: 'DRAFT',
    actorId: 'usr-purchaser-01',
    actorName: 'Siti Rahma',
    actorRole: 'PURCHASER',
    action: 'CREATE_PR',
    reason: 'Initial draft created with 3 line items',
    timestamp: '2026-09-14T08:30:00.000Z',
  },
  {
    id: 'aud-002',
    entityType: 'PURCHASE_REQUEST',
    entityId: 'pr-001',
    fromStatus: 'DRAFT',
    toStatus: 'SUBMITTED',
    actorId: 'usr-purchaser-01',
    actorName: 'Siti Rahma',
    actorRole: 'PURCHASER',
    action: 'SUBMIT_PR',
    reason: 'Submitted for Owner approval',
    timestamp: '2026-09-14T08:45:00.000Z',
  },
  {
    id: 'aud-003',
    entityType: 'PURCHASE_REQUEST',
    entityId: 'pr-002',
    fromStatus: 'SUBMITTED',
    toStatus: 'PARTIALLY_APPROVED',
    actorId: 'usr-owner-01',
    actorName: 'Budi Santoso (Owner)',
    actorRole: 'OWNER',
    action: 'LINE_ITEM_APPROVAL',
    reason: 'Approved stretch film; rejected wooden pallets due to surplus',
    timestamp: '2026-09-12T14:30:00.000Z',
  },
  {
    id: 'aud-004',
    entityType: 'PURCHASE_REQUEST',
    entityId: 'pr-003',
    fromStatus: 'SUBMITTED',
    toStatus: 'APPROVED',
    actorId: 'usr-owner-01',
    actorName: 'Budi Santoso (Owner)',
    actorRole: 'OWNER',
    action: 'LINE_ITEM_APPROVAL',
    reason: 'All items approved for quarterly procurement',
    timestamp: '2026-09-10T15:20:00.000Z',
  },
  {
    id: 'aud-005',
    entityType: 'PURCHASE_REQUEST',
    entityId: 'pr-004',
    fromStatus: null,
    toStatus: 'DRAFT',
    actorId: 'usr-purchaser-01',
    actorName: 'Siti Rahma',
    actorRole: 'PURCHASER',
    action: 'CREATE_PR',
    reason: 'Draft PR created',
    timestamp: '2026-09-14T11:00:00.000Z',
  },
];

const auditLogsDatabase: PRAuditRecord[] = loadPersistentData('pr-audit-logs.json', initialAuditLogsDatabase);
export function getPRs(statusFilter?: string, search?: string): PRRecord[] {
  let list = [...prDatabase];
  if (statusFilter && statusFilter !== 'ALL') {
    list = list.filter((pr) => pr.status === statusFilter);
  }
  if (search) {
    const s = search.toLowerCase();
    list = list.filter(
      (pr) =>
        pr.prNumber.toLowerCase().includes(s) ||
        pr.requesterName.toLowerCase().includes(s) ||
        (pr.notes && pr.notes.toLowerCase().includes(s)) ||
        pr.department.toLowerCase().includes(s)
    );
  }
  return list.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

export function getPRById(id: string): PRRecord | undefined {
  return prDatabase.find((pr) => pr.id === id || pr.prNumber === id);
}

export function getPRAuditLogs(prId: string): PRAuditRecord[] {
  return auditLogsDatabase
    .filter((log) => log.entityId === prId)
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
}

export interface PendingPRItemCandidate {
  prItemId: string;
  prId: string;
  prNumber: string;
  prTitle: string;
  prDate: string;
  department: string;
  itemId: string;
  itemCode: string;
  itemName: string;
  category: string;
  requestedQty: number;
  uomCode: string;
  estimatedPrice: number;
  notes?: string;
  status: PRItemStatus;
}

export function getPendingPRItems(): PendingPRItemCandidate[] {
  const candidates: PendingPRItemCandidate[] = [];

  for (const pr of prDatabase) {
    for (const item of pr.items) {
      // Only include items that are PENDING or REJECTED and have not yet been re-applied to another PR
      if ((item.status === PRItemStatus.PENDING || item.status === PRItemStatus.REJECTED) && !item.reappliedPrId) {
        candidates.push({
          prItemId: item.id,
          prId: pr.id,
          prNumber: pr.prNumber,
          prTitle: pr.title || pr.prNumber,
          prDate: pr.date,
          department: pr.department,
          itemId: item.itemId,
          itemCode: item.itemCode,
          itemName: item.itemName,
          category: item.category,
          requestedQty: item.requestedQty,
          uomCode: item.uomCode,
          estimatedPrice: item.estimatedPrice,
          notes: item.notes,
          status: item.status,
        });
      }
    }
  }

  return candidates;
}

export { prDatabase, auditLogsDatabase };

let logCounter = 1000;

export function addAuditLog(log: Omit<PRAuditRecord, 'id' | 'timestamp'>): PRAuditRecord {
  logCounter += 1;
  const newLog: PRAuditRecord = {
    ...log,
    id: `aud-${Date.now()}-${logCounter}`,
    timestamp: new Date(Date.now() + logCounter).toISOString(),
  };
  auditLogsDatabase.push(newLog);
  savePersistentData('pr-audit-logs.json', auditLogsDatabase);
  syncAuditLogToDatabase(newLog);
  return newLog;
}

let prUniqueCounter = 100;

export function generatePRNumber(): string {
  prUniqueCounter += 1;
  const dateStr = new Date().toISOString().slice(0, 7).replace('-', '');
  const count = prDatabase.length + 1;
  return `PR-${dateStr}-${String(count).padStart(3, '0')}-${prUniqueCounter}`;
}

export function createPR(data: {
  title?: string;
  requesterId: string;
  requesterName: string;
  department: string;
  notes?: string;
  items: Array<{
    itemId: string;
    requestedQty: number;
    notes?: string;
    estimatedPrice?: number;
    reappliedFromPrItemId?: string;
  }>;
  status?: PRStatus;
  submitImmediately?: boolean;
}): PRRecord {
  prUniqueCounter += 1;
  const id = `pr-${Date.now()}-${prUniqueCounter}`;
  const shouldSubmit = data.submitImmediately || data.status === PRStatus.SUBMITTED;
  const prNumber = shouldSubmit ? generatePRNumber() : '[Draf Belum Terbit]';
  const date = new Date().toISOString();

  // Invariant protection: No duplicate items in a single PR
  const itemIds = data.items.map((it) => it.itemId);
  if (new Set(itemIds).size !== itemIds.length) {
    throw new Error('Tidak boleh ada item material yang sama (duplikat) dalam satu Purchase Request. Gabungkan kuantitas pada satu baris.');
  }

  const allItems = getAllMasterItems();
  const formattedItems: PRItemRecord[] = data.items.map((it, idx) => {
    const catalogItem = allItems.find((m) => m.id === it.itemId || m.code === it.itemId) || MASTER_ITEMS.find((m) => m.id === it.itemId || m.code === it.itemId) || {
      id: it.itemId,
      code: `ITM-RAW-${idx + 1}`,
      name: `Custom Material ${idx + 1}`,
      category: 'General',
      defaultUomId: 'uom-001',
      uomCode: 'PCS',
      defaultPrice: it.estimatedPrice || 10000,
    };

    return {
      id: `pri-${id}-${idx + 1}`,
      prId: id,
      itemId: catalogItem.id,
      itemCode: catalogItem.code,
      itemName: catalogItem.name,
      category: catalogItem.category,
      requestedQty: Number(it.requestedQty),
      approvedQty: 0,
      allocatedQty: 0,
      uomId: catalogItem.defaultUomId,
      uomCode: catalogItem.uomCode,
      estimatedPrice: it.estimatedPrice !== undefined ? Number(it.estimatedPrice) : catalogItem.defaultPrice,
      status: PRItemStatus.PENDING,
      notes: it.notes,
    };
  });

  // Link any re-applied items to their origin PR item
  for (const it of data.items) {
    if (it.reappliedFromPrItemId) {
      const origItem = prDatabase.flatMap((p) => p.items).find((i) => i.id === it.reappliedFromPrItemId);
      if (origItem) {
        origItem.reappliedPrId = id;
        origItem.reappliedPrNumber = prNumber;
        origItem.reappliedAt = date;
      }
    } else if (it.notes && it.notes.includes('Re-pengajuan dari PR-')) {
      const match = it.notes.match(/Re-pengajuan dari (PR-[A-Za-z0-9-]+)/);
      if (match) {
        const sourcePrNo = match[1];
        const sourcePR = prDatabase.find((p) => p.prNumber === sourcePrNo || p.id === sourcePrNo);
        const sourceItem = sourcePR?.items.find((i) => i.itemId === it.itemId && !i.reappliedPrId);
        if (sourceItem) {
          sourceItem.reappliedPrId = id;
          sourceItem.reappliedPrNumber = prNumber;
          sourceItem.reappliedAt = date;
        }
      }
    }
  }

  const initialStatus = shouldSubmit ? PRStatus.SUBMITTED : (data.status || PRStatus.DRAFT);

  const newPR: PRRecord = {
    id,
    prNumber,
    title: data.title || `Pengadaan Material ${data.department}`,
    date,
    requesterId: data.requesterId,
    requesterName: data.requesterName,
    department: data.department || 'Procurement',
    notes: data.notes,
    status: initialStatus,
    items: formattedItems,
    isRevision: false,
    createdAt: date,
    updatedAt: date,
  };

  prDatabase.push(newPR);
  savePersistentData('pr-database.json', prDatabase);
  syncPRToDatabase(newPR);

  addAuditLog({
    entityType: 'PURCHASE_REQUEST',
    entityId: id,
    fromStatus: null,
    toStatus: initialStatus,
    actorId: data.requesterId,
    actorName: data.requesterName,
    actorRole: 'PURCHASER',
    action: initialStatus === PRStatus.SUBMITTED ? 'CREATE_AND_SUBMIT_PR' : 'CREATE_PR',
    reason: `Initial ${initialStatus} created with ${formattedItems.length} items`,
  });

  return newPR;
}

export function updatePR(
  id: string,
  data: {
    title?: string;
    department?: string;
    notes?: string;
    items?: Array<{
      itemId: string;
      requestedQty: number;
      notes?: string;
      estimatedPrice?: number;
    }>;
    submitImmediately?: boolean;
  },
  actor: { id: string; name: string; role: string }
): PRRecord {
  const prIndex = prDatabase.findIndex((p) => p.id === id);
  if (prIndex === -1) {
    throw new Error(`PR not found: ${id}`);
  }

  const existingPR = prDatabase[prIndex];
  if (existingPR.status !== PRStatus.DRAFT) {
    throw new Error(`Cannot edit PR with status ${existingPR.status}. Only DRAFT PRs can be edited.`);
  }

  let formattedItems = existingPR.items;
  if (data.items) {
    // Invariant protection: No duplicate items in a single PR
    const itemIds = data.items.map((it) => it.itemId);
    if (new Set(itemIds).size !== itemIds.length) {
      throw new Error('Tidak boleh ada item material yang sama (duplikat) dalam satu Purchase Request. Gabungkan kuantitas pada satu baris.');
    }

    formattedItems = data.items.map((it, idx) => {
      const catalogItem = MASTER_ITEMS.find((m) => m.id === it.itemId) || {
        id: it.itemId,
        code: `ITM-${idx + 1}`,
        name: `Material ${idx + 1}`,
        category: 'General',
        defaultUomId: 'uom-001',
        uomCode: 'PCS',
        defaultPrice: it.estimatedPrice || 10000,
      };

      return {
        id: `pri-${id}-${idx + 1}`,
        prId: id,
        itemId: catalogItem.id,
        itemCode: catalogItem.code,
        itemName: catalogItem.name,
        category: catalogItem.category,
        requestedQty: Number(it.requestedQty),
        approvedQty: 0,
        allocatedQty: 0,
        uomId: catalogItem.defaultUomId,
        uomCode: catalogItem.uomCode,
        estimatedPrice: it.estimatedPrice !== undefined ? Number(it.estimatedPrice) : catalogItem.defaultPrice,
        status: PRItemStatus.PENDING,
        notes: it.notes,
      };
    });
  }

  const shouldSubmit = !!data.submitImmediately;
  let officialPRNumber = existingPR.prNumber;
  if (shouldSubmit && (!officialPRNumber || officialPRNumber === '[Draf Belum Terbit]' || officialPRNumber.startsWith('DRAF') || officialPRNumber === '-')) {
    officialPRNumber = generatePRNumber();
  }

  const updatedPR: PRRecord = {
    ...existingPR,
    prNumber: officialPRNumber,
    title: data.title !== undefined ? data.title : existingPR.title,
    department: data.department !== undefined ? data.department : existingPR.department,
    notes: data.notes !== undefined ? data.notes : existingPR.notes,
    items: formattedItems,
    status: shouldSubmit ? PRStatus.SUBMITTED : existingPR.status,
    isRevision: shouldSubmit ? false : existingPR.isRevision,
    updatedAt: new Date().toISOString(),
  };

  prDatabase[prIndex] = updatedPR;
  savePersistentData('pr-database.json', prDatabase);
  syncPRToDatabase(updatedPR);

  addAuditLog({
    entityType: 'PURCHASE_REQUEST',
    entityId: id,
    fromStatus: PRStatus.DRAFT,
    toStatus: updatedPR.status,
    actorId: actor.id,
    actorName: actor.name,
    actorRole: actor.role,
    action: shouldSubmit ? 'SUBMIT_PR' : 'UPDATE_PR',
    reason: shouldSubmit ? 'PR submitted for Owner approval review' : 'Updated PR details and line items',
    metadata: {
      prNumber: officialPRNumber,
      title: updatedPR.title,
    },
  });

  return updatedPR;
}

export function submitPR(id: string, actor: { id: string; name: string; role: string }): PRRecord {
  const prIndex = prDatabase.findIndex((p) => p.id === id);
  if (prIndex === -1) {
    throw new Error(`PR not found: ${id}`);
  }

  const existingPR = prDatabase[prIndex];
  if (existingPR.status !== PRStatus.DRAFT) {
    throw new Error(`Cannot submit PR with status ${existingPR.status}. Only DRAFT PRs can be submitted.`);
  }

  let officialPRNumber = existingPR.prNumber;
  if (!officialPRNumber || officialPRNumber === '[Draf Belum Terbit]' || officialPRNumber.startsWith('DRAF') || officialPRNumber === '-') {
    officialPRNumber = generatePRNumber();
  }

  const updatedPR: PRRecord = {
    ...existingPR,
    prNumber: officialPRNumber,
    status: PRStatus.SUBMITTED,
    isRevision: false,
    updatedAt: new Date().toISOString(),
  };

  prDatabase[prIndex] = updatedPR;
  savePersistentData('pr-database.json', prDatabase);
  syncPRToDatabase(updatedPR);

  addAuditLog({
    entityType: 'PURCHASE_REQUEST',
    entityId: id,
    fromStatus: PRStatus.DRAFT,
    toStatus: PRStatus.SUBMITTED,
    actorId: actor.id,
    actorName: actor.name,
    actorRole: actor.role,
    action: 'SUBMIT_PR',
    reason: 'PR submitted for Owner approval review',
    metadata: {
      prNumber: officialPRNumber,
      title: updatedPR.title,
    },
  });

  return updatedPR;
}

/**
 * Approver requests revision on a submitted PR.
 * Returns the PR back to DRAFT with isRevision=true and records the revision notes,
 * allowing the requester to edit and resubmit.
 */
export function requestPRRevision(
  id: string,
  notes: string,
  actor: { id: string; name: string; role: string }
): PRRecord {
  const prIndex = prDatabase.findIndex((p) => p.id === id);
  if (prIndex === -1) {
    throw new Error(`PR '${id}' tidak ditemukan.`);
  }

  const existingPR = prDatabase[prIndex];
  if (existingPR.status !== PRStatus.SUBMITTED && existingPR.status !== PRStatus.WAITING_APPROVAL) {
    throw new Error(`Hanya PR yang sedang menunggu persetujuan yang dapat diminta revisi.`);
  }

  const previousStatus = existingPR.status;
  const updatedPR: PRRecord = {
    ...existingPR,
    status: PRStatus.DRAFT,
    isRevision: true,
    revisionNotes: notes,
    updatedAt: new Date().toISOString(),
    // Reset item verdicts back to PENDING so evaluation is clean after revision
    items: existingPR.items.map((it) => ({
      ...it,
      status: PRItemStatus.PENDING,
      approvedQty: 0,
      rejectionReason: undefined,
    })),
  };

  prDatabase[prIndex] = updatedPR;
  savePersistentData('pr-database.json', prDatabase);
  syncPRToDatabase(updatedPR);

  addAuditLog({
    entityType: 'PURCHASE_REQUEST',
    entityId: id,
    fromStatus: previousStatus,
    toStatus: PRStatus.DRAFT,
    actorId: actor.id,
    actorName: actor.name,
    actorRole: actor.role,
    action: 'REQUEST_REVISION',
    reason: notes,
    metadata: {
      prNumber: existingPR.prNumber,
      title: existingPR.title,
      revisionNotes: notes,
    },
  });

  return updatedPR;
}

export function deletePR(id: string, actor: { id: string; name: string; role: string }): boolean {
  const prIndex = prDatabase.findIndex((p) => p.id === id);
  if (prIndex === -1) {
    throw new Error(`PR not found: ${id}`);
  }

  const existingPR = prDatabase[prIndex];
  if (existingPR.status !== PRStatus.DRAFT) {
    throw new Error(`Cannot delete PR with status ${existingPR.status}. Only DRAFT PRs can be deleted.`);
  }

  addAuditLog({
    entityType: 'PURCHASE_REQUEST',
    entityId: id,
    fromStatus: PRStatus.DRAFT,
    toStatus: null,
    actorId: actor.id,
    actorName: actor.name,
    actorRole: actor.role,
    action: 'DELETE_PR',
    reason: 'Draft PR deleted by user',
  });

  prDatabase.splice(prIndex, 1);
  savePersistentData('pr-database.json', prDatabase);
  return true;
}

export function approvePRLineItems(
  id: string,
  decisions: Array<{
    itemId: string;
    verdict: 'APPROVED' | 'REJECTED';
    approvedQty?: number;
    reason?: string;
  }>,
  actor: { id: string; name: string; role: string }
): PRRecord {
  const prIndex = prDatabase.findIndex((p) => p.id === id);
  if (prIndex === -1) {
    throw new Error(`PR not found: ${id}`);
  }

  const existingPR = prDatabase[prIndex];
  if (
    existingPR.status !== PRStatus.SUBMITTED &&
    existingPR.status !== PRStatus.WAITING_APPROVAL &&
    existingPR.status !== PRStatus.DRAFT
  ) {
    throw new Error(
      `Cannot approve PR with status ${existingPR.status}. Only SUBMITTED or WAITING_APPROVAL PRs can be reviewed.`
    );
  }

  // Update line items based on decisions
  const updatedItems = existingPR.items.map((item) => {
    const dec = decisions.find((d) => d.itemId === item.id);
    if (!dec) return item;

    if (dec.verdict === 'APPROVED') {
      const qty = dec.approvedQty !== undefined && dec.approvedQty > 0 ? Number(dec.approvedQty) : item.requestedQty;
      return {
        ...item,
        status: PRItemStatus.APPROVED,
        approvedQty: qty,
        rejectionReason: undefined,
      };
    } else {
      return {
        ...item,
        status: PRItemStatus.REJECTED,
        approvedQty: 0,
        rejectionReason: dec.reason || 'Rejected by approver',
      };
    }
  });

  // Calculate aggregated status using Directive 1 logic
  const allApproved = updatedItems.every((i) => i.status === PRItemStatus.APPROVED);
  const allRejected = updatedItems.every((i) => i.status === PRItemStatus.REJECTED);
  const hasApproved = updatedItems.some((i) => i.status === PRItemStatus.APPROVED);
  const hasRejected = updatedItems.some((i) => i.status === PRItemStatus.REJECTED);
  const hasPending = updatedItems.some((i) => i.status === PRItemStatus.PENDING);

  let newPrStatus: PRStatus;
  if (hasPending) {
    newPrStatus = PRStatus.WAITING_APPROVAL;
  } else if (allRejected) {
    newPrStatus = PRStatus.REJECTED;
  } else if (hasApproved) {
    newPrStatus = PRStatus.APPROVED;
  } else {
    newPrStatus = PRStatus.WAITING_APPROVAL;
  }

  const updatedPR: PRRecord = {
    ...existingPR,
    status: newPrStatus,
    items: updatedItems,
    updatedAt: new Date().toISOString(),
  };

  prDatabase[prIndex] = updatedPR;
  savePersistentData('pr-database.json', prDatabase);
  syncPRToDatabase(updatedPR);

  addAuditLog({
    entityType: 'PURCHASE_REQUEST',
    entityId: id,
    fromStatus: existingPR.status,
    toStatus: newPrStatus,
    actorId: actor.id,
    actorName: actor.name,
    actorRole: actor.role,
    action: 'LINE_ITEM_APPROVAL',
    reason: `Reviewed ${decisions.length} line item(s). PR aggregated to ${newPrStatus}`,
    metadata: { decisions },
  });

  return updatedPR;
}
